import EmblaCarousel from 'embla-carousel';
import ProductCarousel from './product-carousel.js';
import { refreshHeaderCartCount, setHeaderCartCount } from '../utils/cart-count.js';

 class Header {
  constructor() {
    this.header = document.querySelector('[js-header]');
    if(!this.header) {
      return;
    }
    this.onResize = this.onResize.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onSearchInput = this.onSearchInput.bind(this);
    this.searchRequestId = 0;
    this.searchDebounce = null;
    this.init();
  }

  isDesktopViewport() {
    return window.innerWidth > 768;
  }

  getLenis() {
    if (typeof window === 'undefined') return null;
    return window.lenis || null;
  }

  stopLenis() {
    const lenis = this.getLenis();
    if (lenis && typeof lenis.stop === 'function') {
      lenis.stop();
    }
  }

  shouldPreventLenisStart() {
    const body = document.body;
    const isMenuOpen = body.classList.contains('-menu-open');
    const isDesktopMenuOpen = body.classList.contains('-desktop-menu-open');
    const isMiniCartOpen = body.classList.contains('-minicart-open');
    const isQuoteDrawerOpen = body.classList.contains('-quote-drawer-open');

    return isMenuOpen || isDesktopMenuOpen || isMiniCartOpen || isQuoteDrawerOpen;
  }

  closeDrawers() {
    document.body.classList.remove('-minicart-open', '-quote-drawer-open');
    document.querySelectorAll('[js-cart-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  startLenisIfUnlocked() {
    if (this.shouldPreventLenisStart()) return;

    const lenis = this.getLenis();
    if (lenis && typeof lenis.start === 'function') {
      lenis.start();
    }
  }

  closeDesktopSubmenus() {
    this.menuParents.forEach((parent) => {
      parent.setAttribute('aria-expanded', 'false');
    });
    this.panels.forEach((panel) => {
      panel.classList.remove('-active');
    });
    document.body.classList.remove('-desktop-menu-open');
  }

  syncDesktopMenuState() {
    const hasDesktopPanelOpen = this.isDesktopViewport() && Array.from(this.menuParents).some(
      (parent) => parent.getAttribute('aria-expanded') === 'true'
    );

    document.body.classList.toggle('-desktop-menu-open', hasDesktopPanelOpen);

    if (hasDesktopPanelOpen) {
      this.closeDrawers();
      this.stopLenis();
      return;
    }

    this.startLenisIfUnlocked();
  }

  setMobileMenuOpen(nextOpen) {
    if (nextOpen) {
      this.setSearchOpen(false);
      this.closeDrawers();
    }
    document.body.classList.toggle('-menu-open', nextOpen);
    this.menuButton.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');

    if (nextOpen) {
      this.stopLenis();
      return;
    }

    this.startLenisIfUnlocked();
  }

  onResize() {
    this.syncDesktopMenuState();
    if (this.isDesktopViewport()) {
      this.setMobileMenuOpen(false);
    }
  }

  setSearchOpen(nextOpen) {
    if (!this.searchRoot || !this.searchInput || !this.searchToggles.length) return;

    if (nextOpen) {
      this.closeDesktopSubmenus();
      this.setMobileMenuOpen(false);
      this.closeDrawers();
      document.body.classList.add('-search-open');
      this.searchToggles.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.stopLenis();
      setTimeout(() => this.searchInput.focus(), 0);
      return;
    }

    document.body.classList.remove('-search-open');
    this.searchToggles.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    this.searchInput.value = '';
    this.updatePopularSearchesVisibility('');
    this.renderSearchState('');
    this.startLenisIfUnlocked();
  }

  updatePopularSearchesVisibility(query) {
    if (!this.popularSearches) return;
    this.popularSearches.hidden = Boolean(query);
  }

  renderSearchState(markup) {
    if (!this.searchResults) return;
    this.searchResults.innerHTML = markup;
    this.searchResults.hidden = !markup;
  }

  getSearchSkeletonMarkup() {
    const skeletonItemsCount = this.isDesktopViewport() ? 6 : 4;
    const items = Array.from({ length: skeletonItemsCount }, () => `
      <div class="ird-product-carousel__item ird-header__search-skeleton-item">
        <div class="ird-header__search-skeleton-media"></div>
        <div class="ird-header__search-skeleton-data">
          <div class="ird-header__search-skeleton-line -title"></div>
          <div class="ird-header__search-skeleton-line -price"></div>
        </div>
      </div>
    `).join('');

    return `
      <div class="ird-product-carousel ird-header__search-carousel ird-header__search-skeleton" aria-hidden="true">
        <div class="ird-product-carousel__container">
          <div class="ird-product-carousel__slider">
            <div class="ird-product-carousel__items">
              ${items}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  async loadLiveSearch(query) {
    if (!query) {
      this.renderSearchState('');
      return;
    }

    const requestId = ++this.searchRequestId;
    this.renderSearchState(this.getSearchSkeletonMarkup());

    try {
      const params = new URLSearchParams({
        q: query,
        section_id: 'predictive-search',
        type: 'product',
      });
      const response = await fetch(`${window.routes.search_url}?${params.toString()}`);
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const html = await response.text();
      if (requestId !== this.searchRequestId) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const wrapper = doc.querySelector('#shopify-section-predictive-search')
        || doc.querySelector('[id^="shopify-section-"]')
        || doc.body;
      const innerHtml = (wrapper?.innerHTML || '').trim();

      if (!innerHtml) {
        this.renderSearchState('<div class="ird-header__search-state">No results</div>');
        return;
      }

      this.renderSearchState(innerHtml);
      new ProductCarousel();
    } catch (error) {
      if (requestId !== this.searchRequestId) return;
      this.renderSearchState('<div class="ird-header__search-state">Unable to load results</div>');
      console.error(error);
    }
  }

  onSearchInput(event) {
    const query = event.target.value.trim();
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.updatePopularSearchesVisibility(query);
    if (query.length < 2) {
      this.renderSearchState('');
      return;
    }
    this.searchDebounce = setTimeout(() => this.loadLiveSearch(query), 180);
  }

  selectPopularSearch(query) {
    if (!query || !this.searchInput) return;
    this.searchInput.value = query;
    this.updatePopularSearchesVisibility(query);
    this.searchInput.focus();
    this.loadLiveSearch(query);
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;

    const isMobileMenuOpen = document.body.classList.contains('-menu-open');
    const isDesktopMenuOpen = document.body.classList.contains('-desktop-menu-open');

    if (isDesktopMenuOpen) {
      this.closeDesktopSubmenus();
    }

    if (isMobileMenuOpen) {
      this.setMobileMenuOpen(false);
      this.menuAccordions.forEach((accordion) => {
        accordion.setAttribute('aria-expanded', 'false');
      });
    }

    if (isDesktopMenuOpen || isMobileMenuOpen) {
      this.startLenisIfUnlocked();
    }

    if (document.body.classList.contains('-search-open')) {
      this.setSearchOpen(false);
    }
  }

  attachEvents() {
    this.menuParents.forEach((parent,j) => {
      parent.addEventListener('click', (event) =>  {
        event.preventDefault();
        this.panels.forEach(panel => {
          panel.classList.remove('-active');
        });
        if(parent.getAttribute('aria-expanded') === 'true') {
          parent.setAttribute('aria-expanded', 'false');
          this.syncDesktopMenuState();
          return;
        }
        this.menuParents.forEach(parent => {
          parent.setAttribute('aria-expanded', 'false');
        });
        this.menuAccordions.forEach(accordion => {
          accordion.setAttribute('aria-expanded', 'false');
        });
        parent.setAttribute('aria-expanded', 'true');
        this.panels[j].classList.add('-active');
        this.syncDesktopMenuState();
      });
    });

    this.backs.forEach(back => {
      back.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeDesktopSubmenus();
        this.startLenisIfUnlocked();
      });
    });

    this.colBacks.forEach(colBack => {
      colBack.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeDesktopSubmenus();
        this.startLenisIfUnlocked();
      });
    });

    this.menuAccordions.forEach(accordion => {
      accordion.addEventListener('click', (event) => {
        event.preventDefault();
        const isExpanded = accordion.getAttribute('aria-expanded') === 'true';
        accordion.setAttribute('aria-expanded', !isExpanded);
        this.menuAccordions.forEach(acc => {
          if(acc !== accordion) {
            acc.setAttribute('aria-expanded', 'false');
          }
        });
      });
    });

    this.menuButton.addEventListener('click', (event) => {
      event.preventDefault();
      const nextOpen = !document.body.classList.contains('-menu-open');
      this.setMobileMenuOpen(nextOpen);
    });

    if (this.searchCloseButton) {
      this.searchCloseButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.setSearchOpen(false);
      });
    }

    this.searchToggles.forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        const nextOpen = !document.body.classList.contains('-search-open');
        this.setSearchOpen(nextOpen);
      });
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.onSearchInput);
    }

    this.popularSearchButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.selectPopularSearch(button.dataset.searchQuery || button.textContent.trim());
      });
    });

    if (this.backdrop) {
      this.backdrop.addEventListener('click', (event) => {
        event.preventDefault();
        this.setSearchOpen(false);
        this.closeDesktopSubmenus();
        this.startLenisIfUnlocked();
      });
    }

    this.panels.forEach((panel) => {
      const panelHandlers = panel.querySelectorAll('[js-header-menu-handle][data-menu-image-index]');
      const panelImages = panel.querySelectorAll('[js-header-menu-image][data-menu-image-index]');

      if (!panelHandlers.length || !panelImages.length) return;

      panelHandlers.forEach((handler) => {
        const imageIndex = handler.getAttribute('data-menu-image-index');
        if (imageIndex === null) return;

        handler.addEventListener('mouseenter', () => {
          panelImages.forEach((image) => {
            image.classList.toggle('-active', image.getAttribute('data-menu-image-index') === imageIndex);
          });
        });

        handler.addEventListener('mouseleave', () => {
          panelImages.forEach((image) => {
            image.classList.remove('-active');
          });
        });
      });
    });

    document.addEventListener('ird:cart:updated', (event) => {
      setHeaderCartCount(event?.detail?.itemCount || 0);
    });

    if (typeof window.subscribe === 'function') {
      this.cartUnsubscribe = window.subscribe('cart-update', (payload) => {
        const nextCount = payload?.cartData?.item_count;
        if (typeof nextCount === 'number') {
          setHeaderCartCount(nextCount);
        } else {
          refreshHeaderCartCount().catch(() => {});
        }
      });
    }

    window.addEventListener('resize', this.onResize);
    document.addEventListener('keydown', this.onKeydown);
  }



  init() {
    this.menu = this.header.querySelector('[js-header-menu]');
    this.menuParents = this.menu.querySelectorAll('[js-header-menu-parent]');
    this.panels = this.header.querySelectorAll('[js-header-panel]');
    this.menuButton = this.header.querySelector('[js-header-menu-toggle]');
    this.searchRoot = this.header.querySelector('[js-header-search]');
    this.searchInput = this.header.querySelector('[js-header-search-input]');
    this.searchResults = this.header.querySelector('[js-header-search-results]');
    this.searchToggles = this.header.querySelectorAll('[js-search-toggle]');
    this.searchCloseButton = this.header.querySelector('[js-header-search-close]');
    this.popularSearches = this.header.querySelector('[js-header-search-popular]');
    this.popularSearchButtons = this.header.querySelectorAll('[js-header-search-popular-button]');
    this.backdrop = this.header.querySelector('[js-header-desktop-backdrop]');
    this.backs = this.header.querySelectorAll('[js-header-menu-back]');
    this.colBacks = this.header.querySelectorAll('[js-header-menu-col-back]');
    this.menuAccordions = this.header.querySelectorAll('[js-header-menu-accordion]');
    refreshHeaderCartCount().catch(() => {});
    this.syncDesktopMenuState();
    this.attachEvents();
    this.initPopups();
    this.initAnnouncement();
  }

  initAnnouncement() {
    const root = document.querySelector('[js-header-announcement]');
    const viewport = root?.querySelector('[js-header-announcement-viewport]');
    if (!root || !viewport) return;

    const slides = viewport.querySelectorAll('.ird-header__announcement-item');
    if (slides.length < 2) return;

    const embla = EmblaCarousel(viewport, {
      axis: 'y',
      loop: true,
      duration: 25,
      watchDrag: false,
    });

    let timer = null;

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const start = () => {
      stop();
      timer = setInterval(() => embla.scrollNext(), 5000);
    };

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) start();
    });

    start();
  }

  initPopups() {
    this.popupsRoot = document.querySelector('[js-header-popups]');
    if (!this.popupsRoot) return;

    this.popups = Array.from(this.popupsRoot.querySelectorAll('[js-header-popup]'));
    if (!this.popups.length) return;

    const dismissed = this.getDismissedPopups();

    this.popups.forEach((popup) => {
      const id = popup.dataset.popupId;
      if (!id || dismissed.includes(id)) {
        popup.remove();
        return;
      }

      popup.hidden = false;

      const closeButton = popup.querySelector('[js-header-popup-close]');
      if (closeButton) {
        closeButton.addEventListener('click', () => this.dismissPopup(popup));
      }
    });

    if (!this.popupsRoot.querySelector('[js-header-popup]')) {
      this.popupsRoot.remove();
    }
  }

  getDismissedPopups() {
    try {
      const raw = sessionStorage.getItem('ird-header-popups-dismissed');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  setDismissedPopups(ids) {
    try {
      sessionStorage.setItem('ird-header-popups-dismissed', JSON.stringify(ids));
    } catch (e) {
      // Ignore storage errors (private mode, quota, etc.)
    }
  }

  dismissPopup(popup) {
    const id = popup.dataset.popupId;
    if (id) {
      const dismissed = this.getDismissedPopups();
      if (!dismissed.includes(id)) {
        dismissed.push(id);
        this.setDismissedPopups(dismissed);
      }
    }

    popup.remove();

    if (this.popupsRoot && !this.popupsRoot.querySelector('[js-header-popup]')) {
      this.popupsRoot.remove();
    }
  }

}

export default Header;